import { useEffect, useRef } from 'react'

import * as Amplitude from '@amplitude/analytics-browser'
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser'
import { useLocation, useMatches } from 'react-router'

import { SWAGGERHUB_ROLE } from '~/shared/types/shub.ts'
import { UserInfo } from '~/shared/types/user.ts'
import { addEventMetaPlugin } from '~/shared/utils/amplitude/clientPlugins.ts'

interface UseAmplitudeOptions {
  amplitudeApiKey?: string
  user?: UserInfo
  orgId?: string
  appName: string
  isPlatformAdmin?: boolean
  highestRole?: SWAGGERHUB_ROLE
}

export const useAmplitude = ({
  amplitudeApiKey = '',
  user,
  orgId,
  appName,
  isPlatformAdmin,
  highestRole,
}: UseAmplitudeOptions) => {

  const analyticsScriptsLoadedRef = useRef(false)
  const matches = useMatches()
  const location = useLocation()

  const setRouteData = () => {
    // the amplitude execute plugin does not have access to new passed properties
    // so we have to bind it to window values it can pull on demand

    globalThis.window['ENV'].amplitude = {}
    globalThis.window['ENV'].amplitude.orgId = orgId
    globalThis.window['ENV'].amplitude.routeId = matches.at(-1)?.id

    if (appName !== 'Catalog') {
      const userType = () => {
        if (isPlatformAdmin) {
          return 'provider'
        }

        if (highestRole === SWAGGERHUB_ROLE.DESIGNER || highestRole === SWAGGERHUB_ROLE.OWNER) {
          return 'provider'
        }
        return 'consumer'
      }

      globalThis.window['ENV'].amplitude.userType = userType()
      globalThis.window['ENV'].amplitude.productRole = highestRole ?? 'no role'
    }
  }

  const setUser = () => {
    Amplitude.setUserId(user?.analyticsId)

    if (user?.email && !user?.isExternal) {
      const identify = new Amplitude.Identify()
      identify.set('email', user?.email)
      Amplitude.identify(identify)
    }
  }

  useEffect(() => {
    if (amplitudeApiKey?.length &&
      !user?.isExternal &&
      !analyticsScriptsLoadedRef.current) {

      const sessionReplayTracking = sessionReplayPlugin({ sampleRate: 0.8 })
      Amplitude.add(sessionReplayTracking)

      Amplitude.init(amplitudeApiKey,
        {
          fetchRemoteConfig: true,
          autocapture: true,
        },
      )

      void import('@amplitude/engagement-browser').then((mod) => {
        Amplitude.add(mod.plugin())
      })

      setRouteData()

      Amplitude.add(addEventMetaPlugin(appName))

      setUser()

      // due to strict mode, the root application is double loaded.
      // this causes scripts to be loaded twice, so we block the second load
      analyticsScriptsLoadedRef.current = true
    }
  }, [])

  useEffect(() => {
    if (!user?.isExternal) {
      setUser()
    }
  }, [user])

  useEffect(() => {
    setRouteData()
  }, [orgId, location])
}